/**
 * Global MSAL Logout Service
 *
 * This service provides a bridge between non-React code (like error handlers)
 * and React components that have access to the MSAL instance.
 *
 * Usage:
 * - Call `registerMsalLogout(callback)` from a React component with MSAL access
 * - Call `triggerMsalLogout()` from anywhere to trigger the logout
 */

type LogoutCallback = () => void;

let logoutCallback: LogoutCallback | null = null;

/**
 * Register the MSAL logout callback
 * This should be called from a React component that has access to useMsal()
 */
export const registerMsalLogout = (callback: LogoutCallback): void => {
  logoutCallback = callback;
  console.log('[MSAL Logout Service] Logout callback registered');
};

/**
 * Unregister the MSAL logout callback
 * Call this on component unmount to prevent memory leaks
 */
export const unregisterMsalLogout = (): void => {
  logoutCallback = null;
  console.log('[MSAL Logout Service] Logout callback unregistered');
};

/**
 * Trigger MSAL logout
 * Can be called from anywhere, including non-React code
 * Falls back to manual cleanup if callback is not registered
 */
export const triggerMsalLogout = (): void => {
  if (logoutCallback) {
    console.log('[MSAL Logout Service] Triggering registered logout callback');
    logoutCallback();
  } else {
    console.warn('[MSAL Logout Service] No logout callback registered, falling back to manual cleanup');

    // Fallback: manually clear tokens and redirect
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('msal.')) {
        localStorage.removeItem(key);
      }
    });

    window.location.href = '/';
  }
};
